import { PullRequestDetails, ReleaseState } from '@finvari/deliver';

// Whether a merged PR is still waiting to be released — its work is on main but has not reached the
// release branch. This is what the Merged node holds: the release backlog, not a trailing window of
// recent merges, so a PR that merged a fortnight ago and still hasn't shipped stays visible.
//
// Either key is sufficient, and both are needed. The PR's own merge commit is the direct answer, but
// `merge_commit_sha` only becomes the real merge commit when the `closed` webhook re-fetches the PR
// — while it is open GitHub puts a throwaway test-merge commit there — so a missed delivery leaves a
// SHA that matches nothing. The PR number, parsed from the unreleased commits' messages, does not
// depend on the PR document at all and covers that case.
export function isUnreleased(pr: PullRequestDetails, releaseState: ReleaseState | null): boolean {
  // Nothing is claimed either way until the backlog is known — see filterUnreleased.
  if (!releaseState) return false;

  const prNumber = pr.pr?.number;
  if (prNumber !== undefined && releaseState.unreleasedPrNumbers.includes(prNumber)) return true;

  const mergeCommitSha = pr.pr?.merge_commit_sha;
  return !!mergeCommitSha && releaseState.unreleasedShas.includes(mergeCommitSha);
}

// The merged PRs still waiting to be released. Yields nothing until the release state has loaded:
// passing the merged list through unfiltered would first paint a count wrong by the whole of the
// merged window, then correct itself, which reads as a bug rather than a load.
export function filterUnreleased(
  mergedPrs: PullRequestDetails[],
  releaseState: ReleaseState | null
): PullRequestDetails[] {
  if (!releaseState) return [];
  return mergedPrs.filter(pr => isUnreleased(pr, releaseState));
}
